import { useState, useCallback } from 'react';
import { correctEmail } from '../utils/emailCorrection';

/** How long a step's exit animation runs before the next step mounts (`checkout-exit` in Checkout.css). */
export const STEP_TRANSITION_MS = 350;

/**
 * The 3-step checkout's UI state — the part every brand and app shares, free of any cart / API / router:
 * the step with its animated transition, the form data with blur-first validation, the chosen delivery /
 * payment option and the two side panels (privacy, order details). The host hook composes it with its own
 * cart and checkout call (webshop `Clothing/Common/Checkout/useCheckout`, OVER/LL `Checkout/Checkout.jsx`).
 *
 * @param {Object} config
 * @param {Object|null} config.initialFormData - form state at mount ({} or null = no form yet)
 * @param {Object} config.validators  - { field: (value) => errorMessage | '' }; `validateAll` touches these keys
 * @param {number} config.editStep    - the step `handleEditInfo` returns to
 * @param {Object} [config.sanitize]  - { field: (value) => value } applied by `updateField` before storing
 */
const useCheckoutSteps = ({ initialFormData = null, validators = {}, editStep = 2, sanitize = {} } = {}) => {
  /* ── Step state ── */
  const [step, setStep] = useState(1);
  const [leaving, setLeaving] = useState(false);

  /* ── Form data ── */
  const [formData, setFormData] = useState(initialFormData);

  const updateField = useCallback((field) => (e) => {
    const raw = e?.target ? e.target.value : e;
    const value = sanitize[field] ? sanitize[field](raw) : raw;
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, [sanitize]);

  /* ── Validation (blur-first: an error shows once the field was left) ── */
  const [touched, setTouched] = useState({});

  const isFieldValid = useCallback((field) => {
    if (!formData || !formData[field]) return false;
    return !validators[field]?.(formData[field]);
  }, [formData, validators]);

  const fieldError = useCallback((field) => {
    if (!touched[field] || !formData) return '';
    return validators[field]?.(formData[field]) || '';
  }, [formData, touched, validators]);

  const handleBlur = useCallback((field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === 'email') {
      setFormData((prev) => {
        if (!prev?.email) return prev;
        const fixed = correctEmail(prev.email);
        return fixed !== prev.email ? { ...prev, email: fixed } : prev;
      });
    }
  }, []);

  const handleFocus = useCallback((field) => () => {
    setTouched((prev) => ({ ...prev, [field]: false }));
  }, []);

  const validateAll = useCallback(() => {
    if (!formData) return false;
    const allFields = Object.keys(validators);
    setTouched(allFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}));
    return allFields.every((f) => !validators[f](formData[f]));
  }, [formData, validators]);

  /* ── Delivery & payment choice ── */
  const [delivery, setDelivery] = useState('school');
  const [payment, setPayment] = useState(null);

  /* ── Animated step transition ── */
  const goToStep = useCallback((target, { scrollTop = false, beforeTransition } = {}) => {
    setLeaving(true);
    setTimeout(() => {
      if (beforeTransition) beforeTransition();
      setStep(target);
      setLeaving(false);
      if (scrollTop) window.scrollTo({ top: 0, behavior: 'auto' });
    }, STEP_TRANSITION_MS);
  }, []);

  const handleEditInfo = useCallback(() => {
    goToStep(editStep);
  }, [editStep, goToStep]);

  /* ── Side panels ── */
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  return {
    step, leaving, goToStep, handleEditInfo,
    formData, setFormData,
    touched, setTouched,
    updateField,
    isFieldValid, fieldError, handleBlur, handleFocus,
    validateAll,
    delivery, setDelivery,
    payment, setPayment,
    privacyOpen, setPrivacyOpen,
    detailsOpen, setDetailsOpen,
  };
};

export default useCheckoutSteps;
